import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, BriefcaseBusiness, CheckCircle2, LockKeyhole } from 'lucide-react';
import { Link } from 'react-router-dom';
const engagementExamples = [{
  practice: 'CEO Succession',
  sector: 'Industrial & Manufacturing',
  challenge: 'A board needs to define the mandate, align stakeholders, and approach leadership talent without disrupting the incumbent organization.',
  approach: 'Confidential scoping, market mapping, structured executive assessment, and board-led finalist diligence.',
  evidence: 'Detailed client references and engagement outcomes are shared only with appropriate permission and confidentiality safeguards.'
}, {
  practice: 'Cross-Border CFO Search',
  sector: 'Private Equity',
  challenge: 'A portfolio company requires a finance leader with operating depth, transaction fluency, and the ability to lead through a transformation.',
  approach: 'International target-universe research, compensation calibration, candidate assessment, and offer advisory.',
  evidence: 'Candidate and client identities remain protected while a mandate is active or confidential.'
}, {
  practice: 'Board Composition Advisory',
  sector: 'Technology & Growth',
  challenge: 'A leadership team needs to identify the experience gaps that matter most for its next phase of growth.',
  approach: 'Board capability assessment, succession planning, sector intelligence, and independent director outreach.',
  evidence: 'We discuss relevant board-search experience in a confidential scoping conversation, rather than publishing sensitive details without consent.'
}];
export function TestimonialsPage() {
  return <div className="bg-white pt-20" data-id="element-951" data-mp="2pudc9x8ypg5e">
      <section className="bg-navy-900 text-white py-20" data-id="element-952" data-mp="2pr7m0eolk883">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center" data-id="element-953" data-mp="2kt63buv32ax5">
          <div className="flex items-center justify-center gap-3 mb-6" data-id="element-954" data-mp="2fvedd6e00yvl">
            <div className="w-10 h-px bg-gold-500" data-id="element-955" data-mp="2fx7iuicvld20" />
            <span className="text-gold-500 font-semibold tracking-[0.25em] uppercase text-xs" data-id="element-956" data-mp="266h5ryke67te">Search Outcomes</span>
            <div className="w-10 h-px bg-gold-500" data-id="element-957" data-mp="2b7em05lucrs9" />
          </div>
          <h1 className="text-4xl md:text-6xl font-serif font-bold leading-tight mb-6" data-id="element-958" data-mp="1x7c5ont6m6f4">
            Evidence matters more than applause.
          </h1>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed" data-id="element-959" data-mp="218pcb043mdn3">
            Executive search outcomes are often confidential. Rather than publish vague endorsements, we explain the kinds of complex leadership engagements we handle and share relevant references when appropriate.
          </p>
        </div>
      </section>

      <section className="py-12 bg-gold-500/10 border-b border-gold-500/20" data-id="element-960" data-mp="2k6rxt4xftsg5">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 flex flex-col md:flex-row gap-6 md:items-center md:justify-between" data-id="element-961" data-mp="2k4xetw5q8aep">
          <div className="flex items-start gap-3" data-id="element-962" data-mp="3co4d6ezfhpnh">
            <LockKeyhole className="h-6 w-6 text-gold-600 flex-shrink-0 mt-0.5" data-id="element-963" data-mp="3covymwzba701" />
            <div data-id="element-964" data-mp="11711q3b4z5hn">
              <h2 className="font-serif font-bold text-navy-900 text-xl mb-1" data-id="element-965" data-mp="116iy2lm59ozp">Our standard for client proof</h2>
              <p className="text-gray-700 leading-relaxed" data-id="element-966" data-mp="w7oevik9lyf2">We publish named testimonials only with client permission. Confidential mandates are described without exposing people, businesses, or sensitive search details.</p>
            </div>
          </div>
          <Link to="/recruitment-safety" className="inline-flex items-center gap-2 text-navy-900 font-semibold whitespace-nowrap hover:text-gold-600 transition-colors" data-id="element-967" data-mp="iaj2l0tyk3bd">
            Our trust commitment <ArrowRight className="h-4 w-4" data-id="element-968" data-mp="s8ytb2p2pakc" />
          </Link>
        </div>
      </section>

      <section className="py-20 bg-gray-50" data-id="element-969" data-mp="n8kv0cd9xezi">
        <div className="max-w-7xl mx-auto px-6 lg:px-12" data-id="element-970" data-mp="naxbel3dvhag">
          <div className="max-w-3xl mb-12" data-id="element-971" data-mp="23m1bu3aqga75">
            <div className="flex items-center gap-3 mb-5" data-id="element-972" data-mp="23p6xt9o2evyn">
              <div className="w-10 h-px bg-gold-500" data-id="element-973" data-mp="1ymy7ym6hoa0r" />
              <span className="text-gold-600 font-semibold tracking-[0.22em] uppercase text-xs" data-id="element-974" data-mp="86vxarf9vc1b">Representative Engagement Formats</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-serif font-bold text-navy-900 leading-tight" data-id="element-975" data-mp="d6s3mxbfzfk1">How we approach consequential leadership appointments.</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-gray-200" data-id="element-976" data-mp="m6x7ixhffvtg">
            {engagementExamples.map((example, index) => <motion.article key={example.practice} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.08
          }} className="bg-white p-8 md:p-10" data-id="element-977" data-mp="r5hxm42ysz46" data-mpi={index}>
                <BriefcaseBusiness className="h-7 w-7 text-gold-500 mb-7" data-id="element-978" data-mp="19w9fxv4xjzup" />
                <div className="text-xs uppercase tracking-[0.2em] text-gold-600 font-semibold mb-3" data-id="element-979" data-mp="19u50qh1kzef4">{example.sector}</div>
                <h3 className="text-2xl font-serif font-bold text-navy-900 mb-6" data-id="element-980" data-mp="1oz9iir4tuwrx">{example.practice}</h3>
                <div className="space-y-5 text-sm leading-relaxed" data-id="element-981" data-mp="1k06dv7sreskh">
                  <div data-id="element-982" data-mp="1jyv9p6vejisu">
                    <div className="text-xs uppercase tracking-widest text-gray-400 mb-2" data-id="element-983" data-mp="1f13hebquhuo4">The challenge</div>
                    <p className="text-gray-700" data-id="element-984" data-mp="1azro9w8rirb6">{example.challenge}</p>
                  </div>
                  <div data-id="element-985" data-mp="27phfssluit07">
                    <div className="text-xs uppercase tracking-widest text-gray-400 mb-2" data-id="element-986" data-mp="27q0n7fdvdyzz">Our approach</div>
                    <p className="text-gray-700" data-id="element-987" data-mp="22rpcz3jnxmh8">{example.approach}</p>
                  </div>
                  <p className="border-t border-gray-200 pt-5 text-gray-600 italic" data-id="element-988" data-mp="1xyw72ccgk83k">{example.evidence}</p>
                </div>
              </motion.article>)}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" data-id="element-989" data-mp="2qh1uqrt232v5">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center" data-id="element-990" data-mp="2lpjt01j7ky94">
          <CheckCircle2 className="h-8 w-8 text-gold-500 mx-auto mb-6" data-id="element-991" data-mp="2lo8ry9h5e4lp" />
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy-900 mb-5" data-id="element-992" data-mp="jt19qfe8vc1a">Looking for proof relevant to your mandate?</h2>
          <p className="text-lg text-gray-700 leading-relaxed max-w-2xl mx-auto mb-8" data-id="element-993" data-mp="ot5nhxnrpsnt">We will explain our process, discuss applicable experience, and share references when the engagement context and confidentiality requirements allow.</p>
          <Link to="/contact" className="inline-flex items-center gap-2 text-gold-600 font-semibold hover:text-navy-900 transition-colors" data-id="element-994" data-mp="10yzbls74rzh">
            Start a confidential conversation <ArrowRight className="h-4 w-4" data-id="element-995" data-mp="5y8n6wzxsvl7" />
          </Link>
        </div>
      </section>
    </div>;
}